import React, { useState, useEffect } from 'react';
import { Reveal } from './Reveal';
import { MapPin, Calendar, Users, Droplets } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { useSection } from '../contexts/ContentContext';

const statusConfig = {
  active: { color: 'bg-emerald-500', ring: 'ring-emerald-400/40', label: 'Actif', text: 'text-emerald-700', bg: 'bg-emerald-50' },
  planned: { color: 'bg-sky-500', ring: 'ring-sky-400/40', label: 'En projet', text: 'text-sky-700', bg: 'bg-sky-50' },
  study: { color: 'bg-amber-500', ring: 'ring-amber-400/40', label: 'À l\'étude', text: 'text-amber-700', bg: 'bg-amber-50' },
};

export default function KiosqueMap({ compact = false }) {
  const loc = useSection('locations');
  const kiosques = (loc.items || []).map((k, i) => ({ id: i + 1, ...k }));
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (kiosques.length > 0 && !selected) setSelected(kiosques[0]);
  }, [kiosques, selected]);

  if (kiosques.length === 0) return null;
  const sel = selected || kiosques[0];

  return (
    <section className={`relative ${compact ? 'py-16' : 'py-20 lg:py-28'} bg-white overflow-hidden`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {!compact && (
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-12">
              <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">Le réseau</span>
              <h2 className="font-display font-extrabold text-4xl lg:text-5xl text-sky-950 mb-5">
                Les Kiosques <span className="gradient-text">en France</span>
              </h2>
              <p className="text-lg text-slate-600">
                Découvrez le premier kiosque actif à Pau-Lons et les projets en développement partout en France.
              </p>
            </div>
          </Reveal>
        )}

        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-8">
          {/* Map */}
          <Reveal>
            <div className="relative aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/4.6] bg-gradient-to-br from-sky-50 via-white to-emerald-50 rounded-3xl border border-sky-100 overflow-hidden shadow-lg">
              {/* Stylized France map */}
              <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="fg" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0" stopColor="#bae6fd" stopOpacity="0.7" />
                    <stop offset="1" stopColor="#a7f3d0" stopOpacity="0.6" />
                  </linearGradient>
                </defs>
                {/* Simplified France hexagon shape */}
                <path
                  d="M 48 8 Q 58 6 65 12 Q 72 10 78 18 Q 84 22 86 30 Q 88 38 84 44 Q 86 52 82 60 Q 86 68 80 76 Q 74 84 64 86 Q 56 90 46 88 Q 36 90 28 84 Q 20 78 18 70 Q 14 62 18 54 Q 14 46 18 38 Q 16 30 22 24 Q 28 16 38 12 Q 42 8 48 8 Z"
                  fill="url(#fg)"
                  stroke="#0ea5e9"
                  strokeWidth="0.3"
                  strokeDasharray="0.6,0.6"
                />
                {/* Corsica */}
                <ellipse cx="92" cy="78" rx="3" ry="5" fill="url(#fg)" stroke="#0ea5e9" strokeWidth="0.3" strokeDasharray="0.6,0.6" />
              </svg>

              {/* Markers */}
              {kiosques.map((k) => {
                const cfg = statusConfig[k.status] || statusConfig.study;
                const isSel = sel.id === k.id;
                return (
                  <button
                    key={k.id}
                    onClick={() => setSelected(k)}
                    className="absolute -translate-x-1/2 -translate-y-1/2 group"
                    style={{ left: `${k.x}%`, top: `${k.y}%` }}
                  >
                    <span className={`relative block w-4 h-4 rounded-full ${cfg.color} ring-4 ${cfg.ring} ${isSel ? 'scale-150' : 'group-hover:scale-125'} transition-transform shadow-lg`}>
                      {k.status === 'active' && (
                        <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-75"></span>
                      )}
                    </span>
                    <span className={`absolute left-1/2 -translate-x-1/2 mt-2 px-2 py-0.5 rounded-md bg-white shadow text-[10px] font-semibold whitespace-nowrap ${isSel ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
                      {k.city}
                    </span>
                  </button>
                );
              })}

              {/* Legend */}
              <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur rounded-2xl shadow-md p-3 text-xs space-y-1.5">
                <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span> Actif</div>
                <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-sky-500"></span> En projet</div>
                <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-amber-500"></span> À l'étude</div>
              </div>
            </div>
          </Reveal>

          {/* Details */}
          <Reveal delay={150}>
            <div className="space-y-4">
              <div className="bg-gradient-to-br from-sky-50 to-emerald-50 rounded-3xl p-6 border border-sky-100">
                <div className="flex items-center justify-between mb-4">
                  <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold ${(statusConfig[sel.status] || statusConfig.study).text} ${(statusConfig[sel.status] || statusConfig.study).bg}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${(statusConfig[sel.status] || statusConfig.study).color}`}></span>
                    {(statusConfig[sel.status] || statusConfig.study).label}
                  </span>
                  <span className="text-xs text-slate-500">{sel.region}</span>
                </div>
                <h3 className="font-display font-extrabold text-2xl text-sky-950 mb-1">Kiosque {sel.name}</h3>
                <p className="text-slate-600 text-sm mb-5 flex items-center gap-1.5">
                  <MapPin className="w-4 h-4" />{sel.city}, France
                </p>

                <div className="grid grid-cols-2 gap-3">
                  <InfoBox icon={Calendar} label="Ouverture" value={sel.openingDate} />
                  <InfoBox icon={Droplets} label="Type d'eau" value={sel.type} />
                  {sel.status === 'active' && (
                    <InfoBox icon={Users} label="Utilisateurs/jour" value={`~${sel.dailyUsers}`} />
                  )}
                </div>
              </div>

              <div className="max-h-72 overflow-y-auto rounded-2xl border border-sky-100 divide-y divide-sky-50">
                {kiosques.map((k) => (
                  <button
                    key={k.id}
                    onClick={() => setSelected(k)}
                    className={`w-full text-left p-4 hover:bg-sky-50/60 transition-colors flex items-center gap-3 ${sel.id === k.id ? 'bg-sky-50' : 'bg-white'}`}
                  >
                    <span className={`w-2.5 h-2.5 rounded-full ${(statusConfig[k.status] || statusConfig.study).color} flex-shrink-0`}></span>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sky-900 text-sm truncate">{k.name}</p>
                      <p className="text-xs text-slate-500 truncate">{k.region}</p>
                    </div>
                    <span className="text-xs text-slate-400 flex-shrink-0">{k.openingDate}</span>
                  </button>
                ))}
              </div>

              {compact && (
                <Link to="/localisations">
                  <Button className="w-full bg-gradient-to-r from-sky-500 to-emerald-500 hover:from-sky-600 hover:to-emerald-600 text-white rounded-xl border-0 mt-2">
                    Voir tous les kiosques
                  </Button>
                </Link>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function InfoBox({ icon: Icon, label, value }) {
  return (
    <div className="bg-white rounded-xl p-3 border border-sky-100">
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase text-slate-500 tracking-wider mb-1">
        <Icon className="w-3 h-3" />{label}
      </div>
      <p className="font-semibold text-sky-900 text-sm">{value}</p>
    </div>
  );
}
